import { useState, useEffect, useRef } from "react";
import { awardToken } from "../../awardtoken.js"; 
import { Link } from "react-router-dom";
import "./Grade2EnglishLesson.css";

// Example: Reuse some generation logic from your existing code
import { getNounsPool } from "../EnglishTool/pool/nounsPool.js";
import { getVerbsPool } from "../EnglishTool/pool/verbsPool.js";
import { getAdjectivesPool } from "../EnglishTool/pool/adjectivesPool.js";
import { getReadingPool } from "../EnglishTool/pool/readingPool.js";
import { asWord } from "../EnglishTool/quizHelpers.js";

// This can be your “shuffle array” utility:
function shuffleArray(arr) {
  const cloned = [...arr];
  for (let i = cloned.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
}

/** 
 * 1) CHAPTER PAGES 
 *    Suppose we define 4 chapters for Grade 2:
 *      1: Nouns
 *      2: Verbs
 *      3: Adjectives
 *      4: Reading
 */
const chapterPages = {
  1: [
    {
      title: <h2>Nouns – Overview</h2>,
      body: (
        <>
          <p>
            A <strong>noun</strong> is a word that names a person, place,
            thing, or idea.
          </p>
          <ul>
            <li>Examples: dog, John, school, happiness.</li>
            <li>Nouns can be common or proper (like "London").</li>
            <li>A noun answers "Who?" or "What?" in a sentence.</li>
          </ul>
        </>
      ),
    },
    {
      title: <h2>Nouns – Practice</h2>,
      body: (
        <>
          <p>Try spotting nouns in sentences!</p>
          <p>
            <em>"The cat runs." → cat is the noun</em>
          </p>
        </>
      ),
    },
  ],

  2: [
    {
      title: <h2>Verbs – Overview</h2>,
      body: (
        <>
          <p>
            A <strong>verb</strong> is an action or state of being. Examples:
            run, jump, eat.
          </p>
          <ul>
            <li>Verbs can show physical actions or mental actions (think).</li>
            <li>Every sentence needs at least one verb.</li>
          </ul>
        </>
      ),
    },
    {
      title: <h2>Verbs – Practice</h2>,
      body: (
        <>
          <p>Spot the verbs in your favorite story!</p>
          <p>
            <em>"She dances happily." → dances is the verb</em>
          </p>
        </>
      ),
    },
  ],

  3: [
    {
      title: <h2>Adjectives – Overview</h2>,
      body: (
        <>
          <p>
            An <strong>adjective</strong> describes a noun. Examples: big,
            happy, three.
          </p>
          <ul>
            <li>They tell us what kind or how many (blue car, two apples).</li>
            <li>They make sentences more interesting!</li>
          </ul>
        </>
      ),
    },
    {
      title: <h2>Adjectives – Practice</h2>,
      body: (
        <>
          <p>Try describing an object with adjectives:</p>
          <p>
            <em>"The tall, red building." → tall, red = adjectives</em>
          </p>
        </>
      ),
    },
  ],

  4: [
    {
      title: <h2>Reading – Overview</h2>,
      body: (
        <>
          <p>
            <strong>Reading comprehension</strong> means understanding the
            main idea and details.
          </p>
          <ul>
            <li>Look for who or what the sentence is about.</li>
            <li>Identify key words that show the main idea.</li>
          </ul>
        </>
      ),
    },
    {
      title: <h2>Reading – Practice</h2>,
      body: (
        <>
          <p>
            Example: "The sun gives us warmth and light." → Main idea: The sun
            is helpful
          </p>
        </>
      ),
    },
  ],
};

/** =========== 2) Chapter -> topic map for quick quiz =========== */
function getTopicForChapter(ch) {
  // So chapter 1 => "nouns", 2 => "verbs", 3 => "adjectives", 4 => "reading"
  switch (ch) {
    case 1:
      return "nouns";
    case 2:
      return "verbs";
    case 3:
      return "adjectives";
    case 4:
      return "reading";
    default:
      return "nouns"; // fallback
  }
}

function generateChapterQuiz(topic, grade, n = 8) {
  const allNouns = getNounsPool(grade);
  const allVerbs = getVerbsPool(grade);
  const allAdjs = getAdjectivesPool(grade);

  let pool = [];
  if (topic === "nouns") {
    pool = allNouns;
  } else if (topic === "verbs") {
    pool = allVerbs;
  } else if (topic === "adjectives") {
    pool = allAdjs;
  } else if (topic === "reading") {
    pool = getReadingPool(grade);
  }

  const shuffledPool = shuffleArray(pool);
  const chosenItems = shuffledPool.slice(0, n);

  const quizArr = chosenItems.map(item => {
    if (topic === "reading") {
      const questionText = `What is the main idea of: "${item.statement}"?`;
      const correctAnswer = item.mainIdea;
      const distractors = [
        "No main idea given",
        "It’s about time",
        "No idea",
        "Something else",
        "Water shortage"
      ];
      const finalDistractors = shuffleArray(distractors)
        .filter(d => d !== correctAnswer)
        .slice(0, 3);

      return {
        question: questionText,
        correctAnswer,
        options: shuffleArray([correctAnswer, ...finalDistractors]),
      };
    } else {
      let questionText = "";
      let distractorSource = [];
      if (topic === "nouns") {
        questionText = "Which of the following is a noun?";
        distractorSource = [...allVerbs, ...allAdjs];
      } else if (topic === "verbs") {
        questionText = "Which of the following is a verb?";
        distractorSource = [...allNouns, ...allAdjs];
      } else {
        questionText = "Which of the following is an adjective?";
        distractorSource = [...allNouns, ...allVerbs];
      }
      // 挑選、去重、並統一字串型態
      const finalDistractors = [];
      while (finalDistractors.length < 3) {
        const rand = distractorSource[Math.floor(Math.random() * distractorSource.length)];
        if (
          asWord(rand) !== asWord(item) &&
          !finalDistractors.map(asWord).includes(asWord(rand))
        ) {
          finalDistractors.push(rand);
        }
      }
      return {
        question: questionText,
        correctAnswer: asWord(item),
        options: shuffleArray([asWord(item), ...finalDistractors.map(asWord)]),
      };
    }
  });

  return quizArr;
}


/** Utility to shuffle an array in-place */


/** 
 * 4) “Generate Final Quiz” => 50 Q from across all 4 topics 
 *    (like your Grade1 version)
 */
function buildFullQuestionBank(grade) {
  // In a real scenario, you might build 75 Q total from all 4 pools.
  // For brevity, let's just do a small slice from each:
  const bigSet = [];

  // 1) from nouns
  const nouns = getNounsPool(grade);
  nouns.forEach(n => {
    const question = "Which is a noun?";
    const correct = n;
    const distractors = ["run","quickly","red","jump","think","happy"];
    let arr = [correct];
    while (arr.length < 4) {
      const d = distractors[Math.floor(Math.random() * distractors.length)];
      if (!arr.includes(d)) arr.push(d);
    }
    bigSet.push({
      question,
      correctAnswer: correct,
      options: shuffleArray(arr),
    });
  });

  // 2) from verbs
  const verbs = getVerbsPool(grade);
  verbs.forEach(v => {
    const question = "Which is a verb?";
    const correct = v;
    const distractors = ["chair","desk","tall","toy","flower","blue"];
    let arr = [correct];
    while (arr.length < 4) {
      const d = distractors[Math.floor(Math.random() * distractors.length)];
      if (!arr.includes(d)) arr.push(d);
    }
    bigSet.push({
      question,
      correctAnswer: correct,
      options: shuffleArray(arr),
    });
  });

  // 3) from adjectives
  const adjs = getAdjectivesPool(grade);
  adjs.forEach(a => {
    const question = "Which is an adjective?";
    const correct = a;
    const distractors = ["run","car","eat","sleep","town","song"];
    let arr = [correct];
    while (arr.length < 4) {
      const d = distractors[Math.floor(Math.random() * distractors.length)];
      if (!arr.includes(d)) arr.push(d);
    }
    bigSet.push({
      question,
      correctAnswer: correct,
      options: shuffleArray(arr),
    });
  });

  // 4) from reading
  const readingArr = getReadingPool(grade);
  readingArr.forEach(r => {
    const question = `What is the main idea of "${r.statement}"?`;
    const correct = r.mainIdea;
    const distractors = ["No main idea", "Time concept", "No idea", "Something else"];
    let arr = [correct];
    while (arr.length < 4) {
      const d = distractors[Math.floor(Math.random() * distractors.length)];
      if (!arr.includes(d)) arr.push(d);
    }
    bigSet.push({
      question,
      correctAnswer: correct,
      options: shuffleArray(arr),
    });
  });

  return bigSet;
}

function generateFinalQuiz(grade, count=50) {
  const allQs = buildFullQuestionBank(grade); 
  const shuffled = shuffleArray(allQs);
  // If the total is small, just slice. Otherwise, you might get 75 -> pick 50
  return shuffled.slice(0, count);
}

/** ====================== MAIN COMPONENT ====================== */
export default function Grade2EnglishLesson() {
  const grade = 2; // This is Grade 2
  // State for chapters, pages, final quiz, etc.
  const [chapter, setChapter] = useState(1);
  const [pageIdx, setPageIdx] = useState(0);
  const [chapDone, setChapDone] = useState(false);

  const [showQuiz, setShowQuiz] = useState(false);
  const [quiz, setQuiz] = useState([]);    // chapter quiz array
  const [qIdx, setQIdx] = useState(0);
  const [qScore, setQScore] = useState(0);
  const [quizFin, setQuizFin] = useState(false);

  // final quiz
  const [showFinal, setShowFinal] = useState(false);
  const [finalQs, setFinalQs] = useState([]);
  const [fIdx, setFIdx] = useState(0);
  const [fScore, setFScore] = useState(0);
  const [fFin, setFFin] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300);
  const hasAwardedTokens = useRef(false);

  // localStorage read on mount => g2English_chapter, g2English_pageIdx
  const [hasCompletedBefore, setHasCompletedBefore] = useState(false);

  useEffect(() => {
    const storedChap = localStorage.getItem("g2English_chapter");
    const storedPage = localStorage.getItem("g2English_pageIdx");
    const done = localStorage.getItem("grade2EnglishComplete"); 
    if (storedChap) setChapter(parseInt(storedChap, 10) || 1);
    if (storedPage) setPageIdx(parseInt(storedPage, 10) || 0);
    if (done === "true") {
      setHasCompletedBefore(true);
      setChapDone(true);
    }
  }, []);

  // whenever chapter/page changes => store 
  useEffect(() => {
    localStorage.setItem("g2English_chapter", String(chapter));
    localStorage.setItem("g2English_pageIdx", String(pageIdx));
  }, [chapter, pageIdx]);

  // if chapDone => store
  useEffect(() => {
    if (chapDone) {
      localStorage.setItem("grade2EnglishComplete", "true");
    }
  }, [chapDone]);

  // timer for final
  useEffect(() => {
    if (showFinal && !fFin && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(t => t - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFFin(true);
    }
  }, [showFinal, fFin, timeLeft]);

  // get current pages
  const currentPages = chapterPages[chapter];
  const isLastPage = pageIdx === currentPages.length - 1;

  // total pages for progress
  function getTotalPages(obj) {
    return Object.values(obj).reduce((sum, arr) => sum + arr.length, 0);
  }
  function getAbsolutePage(obj, ch, pIdx) {
    let pagesBefore = 0;
    for (let c = 1; c < ch; c++) {
      pagesBefore += obj[c].length;
    }
    return pagesBefore + pIdx + 1;
  }
  const totalPages = getTotalPages(chapterPages);
  const absolutePage = getAbsolutePage(chapterPages, chapter, pageIdx);
  const progress = Math.round((absolutePage / totalPages) * 100);

  // Start chapter quiz
  function startChapterQuiz() {
    const topic = getTopicForChapter(chapter); 
    const generated = generateChapterQuiz(topic, grade, 8);
    setQuiz(generated);
    setQIdx(0);
    setQScore(0);
    setQuizFin(false);
    setShowQuiz(true);
  }

  // handle answer
function handleAnswer(opt) {
  if (
    (opt || '').toLowerCase().trim() ===
    (quiz[qIdx].correctAnswer || '').toLowerCase().trim()
  ) {
    setQScore(s => s + 1);
  }
  if (qIdx < quiz.length - 1) {
    setQIdx(i => i + 1);
  } else {
    setQuizFin(true);
  }
}

  function handleMCQuizFinish() {
    if (qScore >= 4) {
      // pass => next chapter or done
      if (chapter < 4) {
        setChapter(ch => ch + 1);
        setPageIdx(0);
      } else {
        setChapDone(true);
      }
    } else {
      alert("Below 50%. Please review and retake!");
    }
    setQuiz([]);
    setQIdx(0);
    setQScore(0);
    setQuizFin(false);
    setShowQuiz(false);
  }

  // final quiz
  function startFinalQuiz() {
    const final = generateFinalQuiz(grade, 50);
    setFinalQs(final);
    setFIdx(0);
    setFScore(0);
    setFFin(false);
    setTimeLeft(300);
    setShowFinal(true);
  }

  function handleFinalAnswer(opt) {
    if (opt === finalQs[fIdx].correctAnswer) {
      setFScore(s => s + 1);
    }
    if (fIdx < finalQs.length - 1) {
      setFIdx(i => i + 1);
    } else {
      setFFin(true);
    }
  }

  // awarding tokens if >=80
useEffect(() => {
  if (fFin && !hasAwardedTokens.current) {
    const percent = (fScore / finalQs.length) * 100;
    if (percent >= 80) {
      alert("You earned a token! (Pretend awarding code here)");
      awardToken();
    }

    // --- Save final quiz result for dashboard ---
    const quizResults = JSON.parse(localStorage.getItem("quizResults") || "{}");
    quizResults["english_g2"] = {
      ...(quizResults["english_g2"] || {}),
      finalQuizScore: fScore,
      finalQuizTotal: finalQs.length,
      lastCompleted: Date.now(),
    };
    localStorage.setItem("quizResults", JSON.stringify(quizResults));
    // -------------------------------------------

    hasAwardedTokens.current = true;
  }
}, [fFin, fScore, finalQs.length]);


  // UI subcomponent
  function ChapterSlide() {
    const page = currentPages[pageIdx];
    return (
      <div>
        <h2>{page.title}</h2>
        <div>{page.body}</div>
        <div style={{ marginTop: 10 }}>
          {pageIdx > 0 && (
            <button className="button1" onClick={() => setPageIdx(p => p - 1)}>Back</button>
          )}
          {!isLastPage && (
            <button className="button1" onClick={() => setPageIdx(p => p + 1)} style={{ marginLeft: 10 }}>
              Next
            </button>
          )}
          {isLastPage && (
            <button className="button1" onClick={startChapterQuiz} style={{ marginLeft: 15 }}>
              Take Chapter {chapter} Quiz
            </button>
          )}
        </div>
      </div>
    );
  }

  function ChapterQuiz() {
    if (!quiz[qIdx]) return <p>Loading quiz...</p>;
    const cQ = quiz[qIdx];

    if (!quizFin) {
      return (
        <div>
          <p>{cQ.question}</p>
          {cQ.options.map((o, i) => (
  <button className="button1" key={i} onClick={() => handleAnswer(asWord(o))}>
    {asWord(o)}
  </button>
))}
          <p>Score: {qScore}/{quiz.length}</p>
        </div>
      );
    } else {
      return (
        <div>
          <h3>Chapter {chapter} Quiz Finished!</h3>
          <p>You scored {qScore} / {quiz.length}.</p>
          <button className="button1" onClick={handleMCQuizFinish}>Continue</button>
        </div>
      );
    }
  }

  function ChapterUI() {
    if (!showQuiz) {
      return <ChapterSlide />;
    } else {
      return <ChapterQuiz />;
    }
  }

  function FinalQuiz() {
    if (!fFin) {
      const cQ = finalQs[fIdx];
      return (
        <div>
          <h2>Final Quiz (50 Q)</h2>
          <p>Time: {Math.floor(timeLeft/60)}:{String(timeLeft%60).padStart(2,"0")}</p>
          <p>Q {fIdx + 1}/{finalQs.length}: {cQ.question}</p>
          {cQ.options.map((o, i) => (
            <button className="button1" key={i} onClick={() => handleFinalAnswer(o)}>
              {o}
            </button>
          ))}
          <p>Score: {fScore}</p>
        </div>
      );
    } else {
      const percent = Math.round((fScore / finalQs.length) * 100);
      return (
        <div>
          <h3>Final Quiz Completed!</h3>
          <p>
            You scored {fScore} / {finalQs.length} → {percent}% correct
          </p>
          <button ClassName="button1" onClick={() => setShowFinal(false)}>All Done!</button>
        </div>
      );
    }
  }

  return (
    <div className="grade2-english-page">
      <div style={{ margin: "10px 0" }}>
        <div style={{ width: "80%", margin: "0 auto", background: "#ddd", borderRadius: 10, height: 20 }}>
          <div
            style={{
              width: `${progress}%`,
              background: "#4caf50",
              height: 20,
              transition: "width 0.3s"
            }}
          />
        </div>
        <p>{progress}% complete</p>
      </div>

      {hasCompletedBefore && !showFinal && !showQuiz && (
        <div style={{ margin: "15px 0", textAlign:"center" }}>
          <p>You previously completed all Grade 2 English lessons. You can jump to any chapter or retake the final quiz.</p>
          <div>
            {[1,2,3,4].map(ch => (
              <button key={ch} 
                className="button1"
                onClick={() => {
                setChapDone(false);
                // localStorage.removeItem("grade2EnglishComplete");
                setChapter(ch);
                setPageIdx(0);
                setShowQuiz(false);
                setShowFinal(false);
              }}>
                Go to Chapter {ch}
              </button>
            ))}
            <button className="button1" onClick={startFinalQuiz}>Go to Final Quiz</button>
          </div>
        </div>
      )}

      {!chapDone && !showFinal && <ChapterUI />}

      {chapDone && !showFinal && (
        <div style={{ textAlign:"center" }}>
          <h3>All Chapters Completed!</h3>
          <button className="button1" onClick={startFinalQuiz}>Start Final Quiz</button>
        </div>
      )}

      {showFinal && <FinalQuiz />}

      <br/>
      <Link to="/english" className="button1">← Back to English Page</Link>
    </div>
  );
}
